import React, { ReactNode } from 'react';
import { ModalsKeys } from 'global';
import { logError } from 'utils/logger';
import { useSelector } from 'react-redux';
import { useModal } from 'hooks/useModal';
import { useMutation } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import { IconButton, Button, Stack, Tooltip } from '@mui/material';
import { UploadFile, Preview, Edit, Save } from '@mui/icons-material';

import { UPSERT_EQUIPMENT } from '../../../../../api';
import { EquipmentActionsMode, FinanceProgramFormValues, UserActionsEvents } from '../../../../../types';

interface Props extends Partial<UserActionsEvents> {
  isEquipmentExist: boolean;
  isEditEquipmentFormMode: boolean;
  onToggleEquipmentFormMode: (status: boolean) => void;
}

export const EquipmentActions = ({
  isEquipmentExist,
  onSyncEquipment,
  onEquipmentCreated,
  onEquipmentUpdated,
  isEditEquipmentFormMode,
  onToggleEquipmentFormMode,
}: Props) => {
  const vo = useSelector((state: any) => state.vo);

  const {
    getValues,
    formState: { isDirty },
  } = useFormContext<FinanceProgramFormValues>();

  const [upsertEquipmentRequest, { loading }] = useMutation(UPSERT_EQUIPMENT, {
    context: { authRequired: true },
    onCompleted(data) {
      if (onEquipmentUpdated) onEquipmentUpdated(data?.upsertEquipment?.id);
    },
  });

  const { handleOpen } = useModal(ModalsKeys.CreateEquipment);
  const { handleOpen: handleOpenUploadEquipmentImagesDialog } = useModal(
    ModalsKeys.UploadImagesForExistingEquipmentDialog
  );
  const { handleOpen: handleOpenPreviewEquipmentImagesDialog } = useModal(
    ModalsKeys.PreviewExistingEquipmentImagesDialog
  );

  const equipmentActionsMode = !isEquipmentExist ? EquipmentActionsMode.Create : EquipmentActionsMode.Update;

  const handleCreateEquipment = () => {
    handleOpen({ onEquipmentCreated, onSyncEquipment });
  };

  const handleUpdateEquipment = async () => {
    if (!isDirty) {
      return onToggleEquipmentFormMode(false);
    }

    try {
      const {
        make,
        model,
        selectedEquipment,
        newUsed,
        year,
        quantity,
        mileage,
        totalWholesaleCost,
        stockId,
        retailPrice,
        serialNumberVin,
        description,
        class: classType,
      } = getValues('equipment');

      const input: any = {
        equipmentId: selectedEquipment,
        dynamicsVendorOpportunityId: vo.dynamicsVendorOpportunityId,
        vendorOpportunityId: vo.vendorOpportunityId,
        classType,
        make: make,
        model: model,
        newUsed: newUsed,
        year: year.toString(),
        quantity: quantity ? Number(quantity) : 1,
      };

      if (serialNumberVin) {
        input.serialNumberVin = serialNumberVin?.trim();
      }

      if (mileage) {
        input.mileage = Number(mileage.replace(/,/g, ''));
      }

      if (stockId) {
        input.stockId = stockId;
      }

      if (description) {
        input.additionalDescription = description;
      }

      if (retailPrice) {
        input.retailPrice = +retailPrice;
      }

      if (totalWholesaleCost) {
        input.totalWholesaleCost = +totalWholesaleCost;
      }

      await upsertEquipmentRequest({ variables: { input } });
      onToggleEquipmentFormMode(false);
    } catch (error) {
      logError(error);
    }
  };

  const equipmentId = getValues('equipment.selectedEquipment');

  const equipmentActionsByMode: Record<EquipmentActionsMode, ReactNode> = {
    [EquipmentActionsMode.Create]: (
      <Button size="small" variant="contained" onClick={handleCreateEquipment}>
        Add Equipment
      </Button>
    ),
    [EquipmentActionsMode.Update]: (
      <>
        {isEditEquipmentFormMode ? (
          <IconButton color="primary" disabled={loading} className="icon-btn-bordered" onClick={handleUpdateEquipment}>
            <Tooltip title="Save Equipment changes">
              <Save />
            </Tooltip>
          </IconButton>
        ) : (
          <Stack direction="row" gap="1rem">
            <Tooltip title="Upload Equipment Images">
              <IconButton
                color="primary"
                disabled={!equipmentId}
                className="icon-btn-bordered"
                onClick={() => {
                  handleOpenUploadEquipmentImagesDialog({ equipmentId });
                }}
              >
                <UploadFile />
              </IconButton>
            </Tooltip>

            <Tooltip title="Preview Equipment Images">
              <IconButton
                color="primary"
                disabled={!equipmentId}
                className="icon-btn-bordered"
                onClick={() => {
                  handleOpenPreviewEquipmentImagesDialog({ equipmentId });
                }}
              >
                <Preview />
              </IconButton>
            </Tooltip>

            <Tooltip title="Update Equipment">
              <IconButton
                size="small"
                color="primary"
                className="icon-btn-bordered"
                onClick={() => {
                  onToggleEquipmentFormMode(true);
                }}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </>
    ),
  };

  return <>{equipmentActionsByMode[equipmentActionsMode]}</>;
};
